import * as React from "react";
import { toast } from "sonner";
import type { Initiative, Person } from "@/lib/types";
import { exportRoadmap, hasActiveFilters, type ExportScope } from "@/lib/export";
import { fetchAllUpdates } from "@/lib/store";
import { inTimelineWindow, timelineWindowLabel } from "@/lib/timeline-window";
import type { Filters } from "./filter-bar";
import { Icon } from "./icons";
import { Popover } from "./primitives";

// Toolbar Export button: the entire roadmap, only what the filters show, or
// (on the timeline) exactly what's on screen in the visible window.
// "Filtered view" is disabled when nothing is filtered — it would be the
// same file as "Entire roadmap".
export function ExportMenu({
  all,
  filtered,
  roster,
  filters,
  query,
  timelineWindowStart = null,
}: {
  all: Initiative[];
  filtered: Initiative[];
  roster: Person[];
  filters: Filters;
  query: string;
  /** Timeline view only: first month of the visible window (null elsewhere). */
  timelineWindowStart?: Date | null;
}) {
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  // State updates land on the next render; a ref blocks a fast second click.
  const runningRef = React.useRef(false);
  const anchorRef = React.useRef<HTMLButtonElement | null>(null);
  const close = React.useCallback(() => setOpen(false), []);
  const filteredOn = hasActiveFilters(filters, query);
  // On the timeline: exactly what's drawn — the filtered set in the visible
  // window. Picked quarters zoom the timeline, same rule it uses.
  const timeline = React.useMemo(() => {
    if (!timelineWindowStart) return null;
    return { items: inTimelineWindow(filtered, timelineWindowStart, filters.quarter), label: timelineWindowLabel(timelineWindowStart, filters.quarter) };
  }, [filtered, filters.quarter, timelineWindowStart]);

  const run = async (scope: ExportScope) => {
    if (runningRef.current) return;
    if (scope === "window" && !timeline) return;
    runningRef.current = true;
    setOpen(false);
    setBusy(true);
    try {
      // A missing updates feed shouldn't block the export — ship the file
      // with blank update columns and say so.
      const updates = await fetchAllUpdates().catch(() => null);
      const initiatives = scope === "all" ? all : scope === "window" ? timeline?.items ?? [] : filtered;
      await exportRoadmap({ scope, initiatives, roster, updates: updates ?? [], filters, query, windowLabel: timeline?.label });
      if (!updates) toast.warning("Exported without weekly updates", { description: "The latest updates couldn't be loaded." });
    } catch (err: any) {
      toast.error("Couldn't export to Excel", { description: String(err?.message ?? err) });
    } finally {
      runningRef.current = false;
      setBusy(false);
    }
  };

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className="filter-chip"
        data-tour="export"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={busy || all.length === 0}
        onClick={() => setOpen(o => !o)}
      >
        <Icon name="download" size={12} /> {busy ? "Exporting…" : "Export"}
      </button>
      <Popover open={open} anchor={anchorRef.current} onClose={close} align="right">
        <div role="menu" aria-label="Export to Excel">
          <div className="popover-section">Export to Excel</div>
          <button type="button" role="menuitem" data-pop-item className="popover-item" onClick={() => run("all")}>
            <span>Entire roadmap</span>
            <span className="opt-count">{all.length}</span>
          </button>
          <button
            type="button"
            role="menuitem"
            data-pop-item
            className="popover-item"
            disabled={!filteredOn}
            title={filteredOn ? undefined : "No filters or search applied"}
            onClick={() => run("filtered")}
          >
            <span>Filtered view</span>
            <span className="opt-count">{filteredOn ? filtered.length : "—"}</span>
          </button>
          {timeline && (
            <button
              type="button"
              role="menuitem"
              data-pop-item
              className="popover-item"
              disabled={timeline.items.length === 0}
              title="What the timeline shows: your filters, limited to the visible window"
              onClick={() => run("window")}
            >
              <span>Timeline: {timeline.label}</span>
              <span className="opt-count">{timeline.items.length}</span>
            </button>
          )}
        </div>
      </Popover>
    </>
  );
}
